import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { chatAPI, personalityAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mic, Send, Volume2, ArrowLeft, Settings, MicOff, Globe, Play, Star, Headphones, MessageCircle, Brain, AudioWaveform, WifiOff, Zap } from 'lucide-react';
import VoiceVisualizer from '@/components/VoiceVisualizer';
import UpgradeModal from '@/components/UpgradeModal';
import { toast } from 'sonner';
import axios from 'axios';
import { selectMaleVoice, selectFemaleVoice, preloadVoices, speakHybrid, stopSpeaking } from '@/services/voiceService';
import { VoiceProcessor, VoiceState, VoiceStateLabels, VADSensitivity, VADSensitivityLabels, VADSensitivityDescriptions } from '@/services/vcce/VCCEAdapter';
// SPRINT 2: Mobile-First modules são inicializados via VCCEPipeline
import { getErrorMessage, isRetryableError } from '@/utils/errorHandler';
import audioRecorder from '@/services/audioRecorder';
import { streamChatMessage } from '@/services/streamingService';

// Refactored chat components
import { 
  MessageBubble, 
  ChatHeader, 
  ChatInput, 
  VoiceFeedback, 
  SuggestionsBar, 
  ProgressFeedback 
} from '@/components/chat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: Clean translation text (remove emojis and duplicates)
const cleanTranslation = (text) => {
  if (!text) return '';
  
  // Remove all emojis
  let cleaned = text.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[\u{1F100}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F910}-\u{1F96B}]|[\u{1F980}-\u{1F9E0}]/gu, '');
  
  // Remove flag emojis
  cleaned = cleaned.replace(/[\u{1F1E0}-\u{1F1FF}]{2}/gu, '');
  
  // Remove common emoji-like characters
  cleaned = cleaned.replace(/[😀-🙏💀-💯🌀-🗿🚀-🛿🤀-🧿🩰-🪿]/gu, '');
  
  // Clean up extra spaces
  cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
  
  return cleaned;
};

// Helper: Clean main message (keep emojis but remove duplicate translation blocks)
const cleanMainMessage = (text) => {
  if (!text) return '';
  
  // Remove translation blocks that might be embedded
  // Pattern: (Portuguese text in parentheses)
  let cleaned = text.replace(/\n\n?\([^)]+(?:Oi|Olá|E aí|Como|Que|Você|Eu|Tudo|Legal|Bom|Boa)[^)]*\)/gi, '');
  
  // Remove labeled translations
  cleaned = cleaned.replace(/\n\n?(?:Tradução|PORTUGUÊS|🇧🇷)[:\s]*[^\n]+/gi, '');
  
  // Remove italic summary blocks at the end
  cleaned = cleaned.replace(/\n\n?"[^"]+"\s*$/g, '');
  
  return cleaned.trim();
};

// Emotional feedback messages in Portuguese
const EMOTIONAL_FEEDBACK = {
  encouragement: [
    "Boa! Continue assim! 💪",
    "Você está evoluindo! 🔥",
    "Excelente prática! ⭐",
    "Mandou bem! 👏",
    "Sua pronúncia está melhorando! 🎯"
  ],
  milestone: [
    "5 mensagens! Você está no ritmo! 🚀",
    "10 mensagens! Incrível dedicação! 🌟",
    "20 mensagens! Você é fera! 🏆"
  ],
  streak: [
    "Dia {streak} de prática! Continue assim! 🔥",
    "Sequência de {streak} dias! Você é disciplinado! 💪"
  ]
};

const getRandomFeedback = (type) => {
  const messages = EMOTIONAL_FEEDBACK[type];
  return messages[Math.floor(Math.random() * messages.length)];
};

const ChatRoom = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [personality, setPersonality] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPreparingSpeech, setIsPreparingSpeech] = useState(false);
  const [isGuided, setIsGuided] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [improvingMessageId, setImprovingMessageId] = useState(null);
  const [showTranslations, setShowTranslations] = useState(false); // Hidden by default
  const [savingWordId, setSavingWordId] = useState(null);
  const [voiceGender, setVoiceGender] = useState('female');
  const [availableVoices, setAvailableVoices] = useState([]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [planStatus, setPlanStatus] = useState(null);
  const [messageCount, setMessageCount] = useState(0);
  const [showProgressMessage, setShowProgressMessage] = useState(false);
  const [visibleTranslations, setVisibleTranslations] = useState({}); // Track which messages show translation
  const [micLanguage, setMicLanguage] = useState('target'); // 'target' or 'native' for speech recognition
  const [voiceSpeed, setVoiceSpeed] = useState(0.85); // Voice speed: 0.5, 0.75, 0.85, 1.0
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState(false); // Visual indicator for avatar
  const [lastSpokenText, setLastSpokenText] = useState(''); // Store last spoken text for replay
  const [isOnline, setIsOnline] = useState(navigator.onLine); // Network status
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  const [lastUserRecording, setLastUserRecording] = useState(null); // Store user's voice recording
  const [isPlayingRecording, setIsPlayingRecording] = useState(false); // Playing user recording
  const [showReplayButton, setShowReplayButton] = useState(false); // Show replay button after voice message
  const [showEmotionalFeedback, setShowEmotionalFeedback] = useState(null); // Emotional feedback message
  const [conversationTopics, setConversationTopics] = useState([]); // Available topics
  const [showTopicSelector, setShowTopicSelector] = useState(true); // Show topic selector at start
  const [selectedTopic, setSelectedTopic] = useState(null); // Selected conversation topic
  const [initialGreeting, setInitialGreeting] = useState(null); // Personalized greeting
  
  // Advanced Voice Processing states
  const [voiceState, setVoiceState] = useState(VoiceState.IDLE);
  const [voiceStateLabel, setVoiceStateLabel] = useState('');
  const [voiceVolume, setVoiceVolume] = useState(0);
  const [noiseSuppressionActive, setNoiseSuppressionActive] = useState(false);
  const voiceProcessorRef = useRef(null);
  
  // NEW: Continuous Conversation Mode states
  const [continuousMode, setContinuousMode] = useState(false);  // Is continuous mode active?
  const [conversationTurn, setConversationTurn] = useState('idle'); // 'idle' | 'user' | 'lira' | 'processing'
  const [autoListenAfterSpeak, setAutoListenAfterSpeak] = useState(true); // Auto-listen after Lira speaks
  const [sessionStartTime, setSessionStartTime] = useState(null); // For metrics
  const [voiceMetrics, setVoiceMetrics] = useState({
    totalTurns: 0,
    avgLatency: 0,
    clarificationsRequested: 0,
    transcriptionConfidences: [],
  });
  const continuousModeRef = useRef(false); // Ref for callbacks
  const autoListenTimerRef = useRef(null); // Timer for auto-listen delay
  
  // NEW: VAD Sensitivity states
  const [vadSensitivity, setVadSensitivity] = useState(VADSensitivity.NORMAL); // Default: 3 seconds
  const [pauseFeedback, setPauseFeedback] = useState({ show: false, message: '', remainingTime: 0 });
  const [showVadSettings, setShowVadSettings] = useState(false);
  
  // NEW: Streaming response states
  const [useStreaming, setUseStreaming] = useState(true); // Enable streaming by default
  const [streamingText, setStreamingText] = useState(''); // Text being streamed
  const [isStreaming, setIsStreaming] = useState(false); // Currently receiving stream
  const streamControllerRef = useRef(null); // Controller to stop streaming
  
  // Conversation mode based on user level
  const conversationMode = React.useMemo(() => {
    const level = user?.level?.toLowerCase() || 'beginner';
    const modes = {
      beginner: { mode: 'assisted', showTranslationAuto: true, label: 'Modo Assistido 🌱' },
      intermediate: { mode: 'conversation', showTranslationAuto: false, label: 'Modo Conversação 🌿' },
      advanced: { mode: 'immersion', showTranslationAuto: false, label: 'Modo Imersão 🌳' }
    };
    return modes[level] || modes.beginner;
  }, [user?.level]);
  
  const messagesEndRef = useRef(null);
  // SPRINT 1: recognitionRef removido - SpeechRecognition agora gerenciado pelo VCCEPipeline
  const synthRef = useRef(window.speechSynthesis);
  const userAudioRef = useRef(null); // Reference to user audio playback
  const currentAudioRef = useRef(null); // Reference to premium TTS audio
  const isListeningRef = useRef(false); // Track listening state for callbacks
  const transcriptRef = useRef(''); // Track transcript for sending
  const inputRef = useRef(null); // Reference to input element for getting current value
  
  // BUG FIX: Ref de controle único para inicialização de saudação
  // Previne múltiplas chamadas a fetchInitialGreeting durante re-renders
  const greetingInitializedRef = useRef(false);
  const lastConversationIdRef = useRef(null);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success('Conexão restaurada!');
    };
    const handleOffline = () => {
      setIsOnline(false);
      toast.error('Sem conexão com a internet');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Dynamic suggestions state
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Fetch personality settings (for voice gender) - KEPT FOR BACKWARDS COMPATIBILITY
  // Note: Now also called in parallel init, this is just a fallback
  const fetchPersonality = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/personality`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.voice_gender) {
        setVoiceGender(response.data.voice_gender);
      }
      if (response.data) {
        setPersonality(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch personality:', error);
    }
  };

  // Default suggestions for beginners (fallback)
  const getDefaultSuggestions = (targetLang) => {
    const suggestions = {
      'English': ['Hello!', 'Nice to meet you', 'I am from Brazil', 'How are you?', 'Thank you'],
      'Spanish': ['¡Hola!', 'Mucho gusto', 'Soy de Brasil', '¿Cómo estás?', 'Gracias'],
      'German': ['Hallo!', 'Freut mich', 'Ich komme aus Brasilien', 'Wie geht es dir?', 'Danke'],
      'French': ['Bonjour!', 'Enchanté', 'Je viens du Brésil', 'Comment allez-vous?', 'Merci'],
      'Chinese': ['你好！', '很高兴认识你', '我来自巴西', '你好吗？', '谢谢'],
    };
    return suggestions[targetLang] || suggestions['English'];
  };

  // Fetch dynamic suggestions based on conversation context
  const fetchDynamicSuggestions = async (lastAiMessage = null, convId = null) => {
    if (user?.level !== 'beginner') return;
    
    setLoadingSuggestions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chat/suggestions`,
        { 
          conversation_id: convId || conversationId,
          last_ai_message: lastAiMessage 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setDynamicSuggestions(response.data.suggestions);
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      // Use defaults on error
      const targetLang = user?.target_language || user?.learning_language || 'English';
      setDynamicSuggestions(getDefaultSuggestions(targetLang));
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Fetch conversation topics
  const fetchConversationTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chat/topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.topics) {
        setConversationTopics(response.data.topics);
      }
    } catch (error) {
      console.error('Failed to fetch topics:', error);
      // Fallback topics
      setConversationTopics([
        { id: 'travel', name: 'Viagens ✈️', name_en: 'Travel' },
        { id: 'movies', name: 'Filmes 🎬', name_en: 'Movies' },
        { id: 'food', name: 'Comida 🍽️', name_en: 'Food' },
        { id: 'free_chat', name: 'Conversa livre 💬', name_en: 'Free Chat' }
      ]);
    }
  };

  // Fetch personalized greeting (Daily Companion Feature)
  const fetchInitialGreeting = async (topic = null) => {
    try {
      const token = localStorage.getItem('token');
      
      // Use the new personalized-greeting endpoint for daily companion experience
      const personalizedResponse = await axios.get(`${API}/chat/personalized-greeting`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000
      });
      
      const greetingData = personalizedResponse.data;
      
      if (greetingData.greeting) {
        setInitialGreeting(greetingData);
        
        // Extract greeting text (may include translation in parentheses)
        let greetingText = greetingData.greeting;
        let greetingTranslation = null;
        
        // If greeting includes translation in parentheses, extract it
        const translationMatch = greetingText.match(/\n\((.*?)\)$/);
        if (translationMatch) {
          greetingTranslation = translationMatch[1];
          // Keep the full greeting for display (includes translation)
        }
        
        // Add greeting as first message if no messages yet
        if (messages.length === 0) {
          const greetingMsg = {
            id: 'greeting-' + Date.now(),
            role: 'assistant',
            content: greetingText,
            translation: greetingTranslation,
            timestamp: new Date().toISOString(),
            days_away: greetingData.days_away,
            greeting_type: greetingData.greeting_type
          };
          setMessages([greetingMsg]);
          
          // Speak only the target language part (before translation)
          const textToSpeak = greetingText.split('\n(')[0];
          setTimeout(() => speakText(textToSpeak), 500);
        }
      }
    } catch (error) {
      console.error('Failed to fetch personalized greeting:', error);
      // Fallback to regular greeting endpoint
      try {
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (conversationId) params.append('conversation_id', conversationId);
        if (topic) params.append('topic', topic);
        
        const response = await axios.get(`${API}/chat/greeting?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        });
        
        if (response.data.greeting && messages.length === 0) {
          const greetingMsg = {
            id: 'greeting-' + Date.now(),
            role: 'assistant',
            content: response.data.greeting,
            timestamp: new Date().toISOString()
          };
          setMessages([greetingMsg]);
          setTimeout(() => speakText(response.data.greeting), 500);
        }
      } catch (fallbackError) {
        console.error('Failed to fetch fallback greeting:', fallbackError);
      }
    }
  };

  // Handle topic selection
  const handleTopicSelect = async (topicId) => {
    setSelectedTopic(topicId);
    setShowTopicSelector(false);
    
    // Set topic for the conversation
    if (conversationId) {
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/chat/set-topic/${conversationId}`, null, {
          headers: { Authorization: `Bearer ${token}` },
          params: { topic: topicId }
        });
      } catch (error) {
        console.error('Failed to set topic:', error);
      }
    }
    
    // Fetch greeting for this topic (only if not already initialized)
    if (!greetingInitializedRef.current) {
      greetingInitializedRef.current = true;
      fetchInitialGreeting(topicId);
    }
  };

  // P0 OPTIMIZATION: Parallel initialization with performance tracking
  useEffect(() => {
    const initStartTime = performance.now();
    
    // Check if guided mode from URL
    const params = new URLSearchParams(window.location.search);
    const guidedParam = params.get('guided') === 'true';
    const topicParam = params.get('topic');
    setIsGuided(guidedParam);
    
    // If topic specified in URL, use it
    if (topicParam) {
      setSelectedTopic(topicParam);
      setShowTopicSelector(false);
    }
    
    // P0 FIX: Run ALL initialization in parallel using Promise.allSettled
    // This prevents one slow call from blocking others
    const parallelInit = async () => {
      const token = localStorage.getItem('token');
      
      // Create all promises
      const promises = [
        // 1. Preload voices (fast, local)
        preloadVoices().then(voices => {
          setAvailableVoices(voices);
          return { name: 'voices', time: performance.now() - initStartTime };
        }),
        
        // 2. Fetch personality (needed for voice gender)
        axios.get(`${API}/personality`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        }).then(res => {
          if (res.data?.voice_gender) setVoiceGender(res.data.voice_gender);
          if (res.data) setPersonality(res.data);
          return { name: 'personality', time: performance.now() - initStartTime };
        }).catch(err => {
          console.warn('Personality fetch failed:', err);
          return { name: 'personality', time: performance.now() - initStartTime, error: true };
        }),
        
        // 3. Fetch plan status
        axios.get(`${API}/plan/status`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        }).then(res => {
          setPlanStatus(res.data);
          return { name: 'planStatus', time: performance.now() - initStartTime };
        }).catch(err => {
          console.warn('Plan status fetch failed:', err);
          return { name: 'planStatus', time: performance.now() - initStartTime, error: true };
        }),
        
        // 4. Fetch conversation topics
        axios.get(`${API}/chat/topics`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000
        }).then(res => {
          setConversationTopics(res.data.topics || []);
          return { name: 'topics', time: performance.now() - initStartTime };
        }).catch(err => {
          console.warn('Topics fetch failed:', err);
          return { name: 'topics', time: performance.now() - initStartTime, error: true };
        }),
      ];
      
      // Add suggestions fetch for beginners
      if (user?.level === 'beginner') {
        const targetLang = user?.target_language || user?.learning_language || 'English';
        setDynamicSuggestions(getDefaultSuggestions(targetLang));
        
        promises.push(
          axios.post(`${API}/chat/suggestions`, 
            { conversation_id: conversationId },
            { 
              headers: { Authorization: `Bearer ${token}` },
              timeout: 5000 
            }
          ).then(res => {
            if (res.data.suggestions?.length > 0) {
              setDynamicSuggestions(res.data.suggestions);
            }
            return { name: 'suggestions', time: performance.now() - initStartTime };
          }).catch(err => {
            console.warn('Suggestions fetch failed:', err);
            return { name: 'suggestions', time: performance.now() - initStartTime, error: true };
          })
        );
      }
      
      // Execute all in parallel
      const results = await Promise.allSettled(promises);
      
      // Log performance metrics for debugging
      const totalTime = performance.now() - initStartTime;
      const metrics = results.map(r => r.status === 'fulfilled' ? r.value : { error: true });
      console.log(`[ChatRoom] Parallel init complete in ${totalTime.toFixed(0)}ms:`, metrics);
      
      // Store metrics for comparison
      if (window.__vocaliraMetrics) {
        window.__vocaliraMetrics.initTime = totalTime;
        window.__vocaliraMetrics.initBreakdown = metrics;
      } else {
        window.__vocaliraMetrics = { initTime: totalTime, initBreakdown: metrics };
      }
    };
    
    parallelInit();
    
    // SIMPLIFIED: No more topic selector - Lira starts automatically
    setShowTopicSelector(false);
    
  }, [user]);
  
  // GREETING INITIALIZATION FIX v2: Compatível com React.StrictMode
  // 
  // PROBLEMA ANTERIOR: StrictMode invoca useEffect duas vezes (mount → cleanup → remount).
  // O cleanup cancelava o setTimeout, e o remount via a ref já setada como true,
  // resultando em saudação NUNCA sendo chamada em conversas novas.
  //
  // SOLUÇÃO: Usar um padrão "commit-after-fetch" onde a ref só é marcada como true
  // APÓS a saudação ser efetivamente requisitada (não cancelada pelo cleanup).
  useEffect(() => {
    // Detecta se é uma nova conversa (reset da flag)
    if (conversationId !== lastConversationIdRef.current) {
      greetingInitializedRef.current = false;
      lastConversationIdRef.current = conversationId;
    }
    
    // Condições para buscar saudação
    const shouldFetchGreeting = 
      conversationId && 
      messages.length === 0 && 
      !loading && 
      !greetingInitializedRef.current;
    
    if (shouldFetchGreeting) {
      let isCancelled = false;
      
      const timer = setTimeout(() => {
        // Só executa se não foi cancelado pelo cleanup do StrictMode
        if (!isCancelled) {
          // Marca como inicializada APÓS confirmar que não foi cancelado
          greetingInitializedRef.current = true;
          console.log('[ChatRoom] Fetching initial greeting (single call guaranteed)');
          fetchInitialGreeting();
        }
      }, 300);
      
      // Cleanup: cancela se o efeito for re-executado antes do timer disparar
      return () => {
        isCancelled = true;
        clearTimeout(timer);
      };
    }
  }, [conversationId, messages.length, loading]);

  const fetchPlanStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/plan/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlanStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch plan status:', error);
    }
  };

  const getAvatarGender = (avatarStyle) => {
    const maleAvatars = ['casual_native', 'neutral_assistant'];
    const femaleAvatars = ['professional_teacher', 'friendly_young'];
    
    if (maleAvatars.includes(avatarStyle)) return 'male';
    if (femaleAvatars.includes(avatarStyle)) return 'female';
    return 'female'; // default
  };

  const getLanguageCode = (language) => {
    const languageCodes = {
      'English': 'en-US',
      'Spanish': 'es-ES',
      'Portuguese': 'pt-BR',
      'French': 'fr-FR',
      'German': 'de-DE',
      'Chinese': 'zh-CN'
    };
    return languageCodes[language] || 'en-US';
  };

  // Voice selection now uses centralized voiceService for consistency

  // LEGACY SPEECH RECOGNITION REMOVED - Sprint 1 STT Lifecycle Fix
  // A instância de SpeechRecognition agora é gerenciada EXCLUSIVAMENTE pelo VCCEPipeline.
  // Isso elimina a possibilidade de duas instâncias competirem pelo microfone
  // e garante um ciclo de vida isolado por turno.
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup continuous mode timer
      if (autoListenTimerRef.current) {
        clearTimeout(autoListenTimerRef.current);
      }
      // Cleanup voice processor
      if (voiceProcessorRef.current) {
        voiceProcessorRef.current.destroy();
      }
    };
  }, []); // Only run once on mount

  // Update language when user or micLanguage changes
  // REMOVED: Legacy language update for recognitionRef
  // O idioma agora é gerenciado pelo VCCEPipeline através do VCCEAdapter.start(langCode)

  useEffect(() => {
    if (conversationId) {
      fetchConversation();
    }
    fetchPersonality();
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversation = async () => {
    try {
      const data = await chatAPI.getConversation(conversationId);
      setMessages(data.messages || []);
    } catch (error) {
      toast.error('Falha ao carregar conversa');
    }
  };

  const handleSendMessage = async (e, voiceText = null, isFromVoice = false, audioMetrics = null) => {
    e?.preventDefault();
    const messageText = voiceText || inputMessage;
    if (!messageText.trim() || loading) return;

    // Check network connection
    if (!isOnline) {
      toast.error('Sem conexão. Verifique sua internet.');
      return;
    }

    // Check if limit reached before sending
    if (planStatus && planStatus.limit_reached) {
      setShowUpgradeModal(true);
      return;
    }

    setInputMessage('');
    setLoading(true);

    // Use streaming for faster responses (if enabled)
    if (useStreaming) {
      try {
        // Add user message immediately for instant feedback
        const userMsg = {
          role: 'user',
          content: messageText,
          timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, userMsg]);
        
        // Reset streaming text
        setStreamingText('');
        setIsStreaming(true);
        
        // Create placeholder for AI message
        const aiPlaceholder = {
          role: 'assistant',
          content: '',
          timestamp: new Date().toISOString(),
          isStreaming: true,
        };
        setMessages(prev => [...prev, aiPlaceholder]);
        
        // Start streaming
        const controller = await streamChatMessage({
          message: messageText,
          conversationId,
          onTextChunk: (chunk, fullText) => {
            // Update the last message (AI placeholder) with accumulated text
            setMessages(prev => {
              const updated = [...prev];
              const lastIdx = updated.length - 1;
              if (updated[lastIdx]?.isStreaming) {
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  content: fullText,
                };
              }
              return updated;
            });
            setStreamingText(fullText);
          },
          onAudioReady: (audio, text, index) => {
            // Audio is handled by the streaming service's AudioQueue
            console.log(`[Streaming] Audio chunk ${index} ready for: ${text.substring(0, 30)}...`);
          },
          onComplete: (result) => {
            setIsStreaming(false);
            setLoading(false);
            
            // P0 FIX: Finalize the AI message with translation from streaming
            setMessages(prev => {
              const updated = [...prev];
              const lastIdx = updated.length - 1;
              if (updated[lastIdx]?.isStreaming) {
                updated[lastIdx] = {
                  ...updated[lastIdx],
                  content: result.fullText,
                  translation: result.translation || null,  // New: include translation
                  isStreaming: false,
                };
              }
              return updated;
            });
            
            // Update conversation context for turn detection
            if (voiceProcessorRef.current) {
              voiceProcessorRef.current.updateConversationContext([
                ...messages,
                userMsg,
                { role: 'assistant', content: result.fullText }
              ]);
            }
            
            // Increment message count
            const newCount = messageCount + 1;
            setMessageCount(newCount);
            
            // Show milestone feedback
            if (newCount === 5 || newCount === 10 || newCount === 20) {
              const milestoneIdx = newCount === 5 ? 0 : newCount === 10 ? 1 : 2;
              showFeedback('milestone', EMOTIONAL_FEEDBACK.milestone[milestoneIdx]);
            } else if (newCount % 3 === 0) {
              showFeedback('encouragement');
            }
            
            // Fetch suggestions for beginners
            if (user?.level === 'beginner') {
              fetchDynamicSuggestions(result.fullText, result.conversationId);
            }
            
            // Reset voice state if not in continuous mode
            if (!continuousModeRef.current) {
              setVoiceState(VoiceState.IDLE);
              if (voiceProcessorRef.current) {
                voiceProcessorRef.current.setIdle();
              }
            }
          },
          onError: (err) => {
            console.error('[Streaming] Error:', err);
            setIsStreaming(false);
            setLoading(false);
            toast.error('Erro na resposta. Tente novamente.');
            
            // Remove the placeholder message on error
            setMessages(prev => prev.filter(m => !m.isStreaming));
          },
          onPlaybackStart: () => {
            setIsSpeaking(true);
            setIsAvatarSpeaking(true);
            setConversationTurn('lira');
            
            // Notify voice processor that Lira is speaking
            if (voiceProcessorRef.current) {
              voiceProcessorRef.current.setLiraSpeaking(true);
            }
          },
          onPlaybackEnd: () => {
            setIsSpeaking(false);
            setIsAvatarSpeaking(false);
            
            // Notify voice processor
            if (voiceProcessorRef.current) {
              voiceProcessorRef.current.setLiraSpeaking(false);
            }
            
            // Auto-listen in continuous mode
            if (continuousModeRef.current && autoListenAfterSpeak) {
              setConversationTurn('waiting');
              autoListenTimerRef.current = setTimeout(() => {
                if (continuousModeRef.current) {
                  startListeningInternal();
                }
              }, 600);
            } else {
              setConversationTurn('idle');
            }
          },
          playAudio: true,
        });
        
        streamControllerRef.current = controller;
        
        // Refresh plan status
        fetchPlanStatus();
        
      } catch (error) {
        console.error('[Streaming] Fatal error:', error);
        setIsStreaming(false);
        setLoading(false);
        toast.error('Erro ao conectar. Tente novamente.');
        
        // Fallback to non-streaming
        setUseStreaming(false);
        handleSendMessage(e, messageText, isFromVoice, audioMetrics);
      }
      return;
    }

    // Non-streaming fallback
    try {
      // Pass isFromVoice flag and audio metrics to enable STT validation on backend
      const response = await chatAPI.sendMessage(
        conversationId, 
        messageText, 
        false, 
        isFromVoice,
        audioMetrics || (voiceProcessorRef.current?.metrics)
      );
      
      // Check if Lira is asking for repetition
      if (response.requested_repetition) {
        toast.info('🎤 Lira não entendeu bem. Fale novamente!', { duration: 3000 });
        
        // Add Lira's repetition request as a message
        const aiRepetitionMessage = {
          role: 'assistant',
          content: response.message,
          timestamp: new Date().toISOString(),
          requested_repetition: true,
        };
        
        setMessages(prev => [...prev, aiRepetitionMessage]);
        
        // If in continuous mode, auto-start listening after a delay
        if (continuousModeRef.current) {
          setTimeout(() => {
            speakText(response.message, true);
          }, 500);
        }
        
        setLoading(false);
        return;
      }
      
      // Reset retry count on success
      setRetryCount(0);
      setRetryCount(0);
      
      // Refresh plan status after successful message
      fetchPlanStatus();
      
      // Increment message count and check for progress milestone
      const newCount = messageCount + 1;
      setMessageCount(newCount);
      
      // Show emotional feedback at milestones
      if (newCount === 5) {
        showFeedback('milestone', EMOTIONAL_FEEDBACK.milestone[0]);
        setShowProgressMessage(true);
        setTimeout(() => setShowProgressMessage(false), 4000);
      } else if (newCount === 10) {
        showFeedback('milestone', EMOTIONAL_FEEDBACK.milestone[1]);
        setShowProgressMessage(true);
        setTimeout(() => setShowProgressMessage(false), 4000);
      } else if (newCount === 20) {
        showFeedback('milestone', EMOTIONAL_FEEDBACK.milestone[2]);
        setShowProgressMessage(true);
        setTimeout(() => setShowProgressMessage(false), 4000);
      } else if (newCount % 3 === 0) {
        // Show encouragement every 3 messages
        showFeedback('encouragement');
      }
      
      // Fetch translation for AI response if different language
      let translatedResponse = null;
      if (user && user.native_language !== (user.target_language || user.learning_language)) {
        try {
          const token = localStorage.getItem('token');
          const transRes = await axios.post(
            `${API}/chat/translate?message=${encodeURIComponent(response.message.content)}`,
            {},
            { 
              headers: { Authorization: `Bearer ${token}` },
              timeout: 10000
            }
          );
          translatedResponse = transRes.data.translation;
        } catch (err) {
          // Translation failed silently - not critical
          console.error('Translation error:', err);
        }
      }
      
      const aiMessageWithTranslation = {
        ...response.message,
        translation: translatedResponse
      };
      
      const newMessages = [
        ...messages,
        response.user_message,
        aiMessageWithTranslation,
      ];
      setMessages(newMessages);
      
      // Fetch new dynamic suggestions based on AI response (for beginners)
      if (user?.level === 'beginner') {
        fetchDynamicSuggestions(response.message.content, response.conversation_id);
      }
      
      // Auto-play audio with 1.5s delay (faster for better UX)
      setIsPreparingSpeech(true);
      const delayTimer = setTimeout(() => {
        setIsPreparingSpeech(false);
        speakText(response.message.content);
      }, 1500);
      
      // Cleanup timer if component unmounts
      return () => clearTimeout(delayTimer);
    } catch (error) {
      // Check if error is due to limit
      if (error.response?.status === 429) {
        setShowUpgradeModal(true);
        fetchPlanStatus();
      } else if (isRetryableError(error) && retryCount < 2) {
        // Auto-retry for network issues (preserve isFromVoice flag)
        setRetryCount(prev => prev + 1);
        toast.error('Tentando novamente...');
        setTimeout(() => handleSendMessage(null, messageText, isFromVoice), 2000);
      } else {
        toast.error(getErrorMessage(error));
        // Restore input if failed
        setInputMessage(messageText);
      }
    } finally {
      setLoading(false);
      // Reset voice state to idle ONLY if not in continuous mode
      // In continuous mode, speakText's onEnd handler will manage the state
      if (!continuousModeRef.current) {
        setVoiceState(VoiceState.IDLE);
        setVoiceStateLabel('');
        if (voiceProcessorRef.current) {
          voiceProcessorRef.current.setIdle();
        }
      }
    }
  };

  // Handle clicking a suggested response
  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    // Auto-send after a small delay
    setTimeout(() => {
      handleSendMessage(null, suggestion);
    }, 100);
  };

  // Toggle translation visibility for a specific message
  const toggleTranslation = (msgId) => {
    setVisibleTranslations(prev => ({
      ...prev,
      [msgId]: !prev[msgId]
    }));
  };

  const toggleListening = async () => {
    // If in continuous mode, toggle that instead
    if (continuousMode) {
      stopContinuousMode();
      return;
    }
    
    // Initialize voice processor if not exists
    if (!voiceProcessorRef.current) {
      await initializeVoiceProcessor();
    }

    if (isListening) {
      // STOP listening
      const transcript = voiceProcessorRef.current.stop();
      
      // Stop recording for playback
      try {
        const recording = await audioRecorder.stopRecording();
        if (recording) {
          setLastUserRecording(recording);
          setShowReplayButton(true);
          setTimeout(() => setShowReplayButton(false), 15000);
        }
      } catch (err) {
        console.warn('Could not save recording:', err);
      }
      
      // Wait for state to update
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Get final transcript
      const messageToSend = transcript || transcriptRef.current?.trim() || '';
      
      if (messageToSend) {
        transcriptRef.current = '';
        setInputMessage('');
        
        // Show interpreting state
        setVoiceState(VoiceState.INTERPRETING);
        setVoiceStateLabel(VoiceStateLabels.interpreting);
        setConversationTurn('processing');
        
        // Send with voice flag and audio metrics for intelligent processing
        handleSendMessage(null, messageToSend, true, voiceProcessorRef.current?.metrics);
      } else {
        toast.info('Nenhuma fala detectada. Tente novamente.');
        voiceProcessorRef.current.setIdle();
      }
      
      transcriptRef.current = '';
      
    } else {
      // START listening (single shot, not continuous)
      await startListeningInternal();
      toast.success('🎤 Fale naturalmente... Aguardarei você terminar', { duration: 3000 });
    }
  };

  /**
   * Internal function to start listening (used by auto-listen)
   * SPRINT 1: Agora chama prepareForNewTurn() para garantir ciclo de vida isolado
   */
  const startListeningInternal = async () => {
    if (isListening || isSpeaking) {
      console.log('[ContinuousMode] Cannot start listening - already listening or speaking');
      return;
    }
    
    // Initialize voice processor if needed
    if (!voiceProcessorRef.current) {
      await initializeVoiceProcessor();
    }
    
    // Stop any ongoing speech
    synthRef.current?.cancel();
    stopSpeaking();
    
    // SPRINT 1: Preparar para novo turno - limpa transcript e destrói recognition antiga
    console.log('[SPRINT1] Preparing for new turn - isolated lifecycle');
    voiceProcessorRef.current.prepareForNewTurn();
    
    // Clear previous input
    setInputMessage('');
    transcriptRef.current = '';
    
    // Set language
    const targetLang = user?.target_language || user?.learning_language || 'English';
    const nativeLang = user?.native_language || 'Portuguese';
    const langToUse = micLanguage === 'native' ? nativeLang : targetLang;
    const langCode = getLanguageCode(langToUse);
    
    try {
      // Start recording for playback
      await audioRecorder.startRecording();
      
      // SPRINT 1: start() agora cria nova instância de SpeechRecognition
      const started = await voiceProcessorRef.current.start(langCode);
      
      if (started) {
        setConversationTurn('user');
        console.log('[SPRINT1] Now listening with fresh SpeechRecognition instance');
      } else {
        console.error('[ContinuousMode] Failed to start voice processor');
        if (continuousModeRef.current) {
          // In continuous mode, show toast but keep mode active
          toast.error('Erro ao iniciar microfone. Toque para tentar novamente.');
        }
      }
    } catch (error) {
      console.error('[ContinuousMode] Error starting listening:', error);
    }
  };

  /**
   * Initialize voice processor with all callbacks including INTELLIGENT TURN DETECTION
   */
  const initializeVoiceProcessor = async () => {
    const targetLang = user?.target_language || user?.learning_language || 'English';
    const nativeLang = user?.native_language || 'Portuguese';
    const langToUse = micLanguage === 'native' ? nativeLang : targetLang;
    
    voiceProcessorRef.current = new VoiceProcessor({
      sensitivity: vadSensitivity, // Apply current sensitivity setting
      intelligentTurnEnabled: true, // Enable intelligent turn detection
      onStateChange: (state, label) => {
        setVoiceState(state);
        setVoiceStateLabel(label);
        
        // Clear pause feedback when leaving detecting_pause state
        if (state !== VoiceState.DETECTING_PAUSE) {
          setPauseFeedback({ show: false, message: '', remainingTime: 0 });
        }
        
        // Map processor state to existing isListening state
        if (state === VoiceState.LISTENING || state === VoiceState.DETECTING_PAUSE) {
          setIsListening(true);
          isListeningRef.current = true;
          setConversationTurn('user');
        } else if (state === VoiceState.PROCESSING || state === VoiceState.INTERPRETING) {
          setIsListening(false);
          isListeningRef.current = false;
          setConversationTurn('processing');
        } else if (state === VoiceState.IDLE) {
          setIsListening(false);
          isListeningRef.current = false;
          if (!continuousModeRef.current) {
            setConversationTurn('idle');
          }
        }
      },
      onTranscript: (text) => {
        transcriptRef.current = text;
        setInputMessage(text);
        
        // Update conversation context for intelligent detection
        if (voiceProcessorRef.current) {
          voiceProcessorRef.current.updateConversationContext(messages);
        }
      },
      onInterimTranscript: (text) => {
        if (text) {
          setInputMessage(prev => {
            const finalPart = transcriptRef.current || '';
            return (finalPart + ' ' + text).trim();
          });
        }
      },
      onVolumeLevel: (level) => {
        setVoiceVolume(level);
      },
      onError: (error) => {
        toast.error(error);
        setVoiceState(VoiceState.IDLE);
        setIsListening(false);
        isListeningRef.current = false;
        setPauseFeedback({ show: false, message: '', remainingTime: 0 });
      },
      onNoiseSuppressed: () => {
        setNoiseSuppressionActive(true);
        setTimeout(() => setNoiseSuppressionActive(false), 1000);
      },
      // NEW: Pause feedback callback - "Ainda ouvindo..."
      onPauseFeedback: (show, message, remainingTime = 0) => {
        setPauseFeedback({ show, message, remainingTime });
      },
      onMetrics: async (metrics) => {
        // Update local metrics
        setVoiceMetrics(prev => ({
          totalTurns: prev.totalTurns + 1,
          avgLatency: prev.avgLatency, // Will be updated by handleSendMessage
          clarificationsRequested: prev.clarificationsRequested,
          transcriptionConfidences: [...prev.transcriptionConfidences.slice(-19), metrics.peakVolume],
        }));
        
        // Send to backend
        try {
          const token = localStorage.getItem('token');
          if (token && metrics.duration > 1000) {
            await axios.post(`${API}/chat/audio-metrics`, {
              duration: metrics.duration,
              speechDetections: metrics.speechDetections,
              noiseSuppressions: metrics.noiseSuppressions,
              peakVolume: metrics.peakVolume,
              avgVolume: metrics.avgVolume,
              transcriptLength: metrics.transcriptLength,
              recognitionRestarts: metrics.recognitionRestarts,
              finalTranscript: metrics.finalTranscript,
              continuousMode: continuousModeRef.current,
              browser: navigator.userAgent.includes('Chrome') ? 'chrome' : 
                       navigator.userAgent.includes('Firefox') ? 'firefox' :
                       navigator.userAgent.includes('Safari') ? 'safari' : 'other',
              deviceInfo: {
                isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
                isAndroid: /Android/.test(navigator.userAgent),
                isMobile: /Mobi/.test(navigator.userAgent),
              }
            }, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 5000
            });
          }
        } catch (err) {
          console.warn('[ChatRoom] Failed to send audio metrics:', err.message || err);
        }
      },
      // INTELLIGENT TURN DETECTION: Called when system detects user finished speaking
      onTurnDetected: async (turnData) => {
        console.log('[IntelligentTurn] Turn detected!', {
          confidence: turnData.confidence,
          reason: turnData.reason,
          isLocal: turnData.isLocal,
        });
        
        const transcript = turnData.transcript || transcriptRef.current?.trim() || '';
        
        if (!transcript) {
          console.log('[IntelligentTurn] No transcript to send');
          return;
        }
        
        // Stop recording for playback
        try {
          const recording = await audioRecorder.stopRecording();
          if (recording) {
            setLastUserRecording(recording);
            setShowReplayButton(true);
            setTimeout(() => setShowReplayButton(false), 15000);
          }
        } catch (err) {
          console.warn('[IntelligentTurn] Could not save recording:', err);
        }
        
        // Clear input and transcript ref
        transcriptRef.current = '';
        setInputMessage('');
        
        // Show interpreting state
        setVoiceState(VoiceState.INTERPRETING);
        setVoiceStateLabel(VoiceStateLabels.interpreting);
        setConversationTurn('processing');
        
        // Check if we need to ask for clarification
        if (turnData.shouldClarify) {
          console.log('[IntelligentTurn] Clarification needed:', turnData.clarificationReason);
          toast.info('🎤 Não entendi bem... pode repetir?', { duration: 3000 });
        }
        
        // Send the message automatically
        handleSendMessage(null, transcript, true, voiceProcessorRef.current?.metrics);
      },
      // BARGE-IN: Called when user interrupts Lira
      onBargeIn: () => {
        console.log('[Barge-In] User wants to speak! Stopping Lira...');
        
        // Immediately stop Lira's speech
        stopSpeaking();
        
        // Clear any auto-listen timer
        if (autoListenTimerRef.current) {
          clearTimeout(autoListenTimerRef.current);
          autoListenTimerRef.current = null;
        }
        
        // Show feedback
        toast.info('🎤 Você quer falar? Estou ouvindo!', { duration: 2000 });
        
        // Transition to listening state
        setConversationTurn('user');
        setVoiceState(VoiceState.LISTENING);
      },
      // LEGACY FALLBACK: Called when VAD auto-detects end of speech
      // NOTA ARQUITETURAL: Com o VCCE ativo, este callback NÃO será chamado porque:
      // 1. VCCEAdapter.intelligentTurnEnabled = true (sempre)
      // 2. VCCEAdapter._handleTurnComplete() chama onTurnDetected (não onAutoFinish)
      // Este código permanece para compatibilidade com processadores de voz alternativos
      onAutoFinish: async (transcript) => {
        // Guard: só processa se intelligent turn detection não estiver ativo
        if (voiceProcessorRef.current?.intelligentTurnEnabled) {
          console.log('[ContinuousMode] Auto-finish ignored - using onTurnDetected channel');
          return;
        }
        
        console.log('[ContinuousMode] Auto-finish triggered with transcript:', transcript?.substring(0, 50));
        
        // Stop recording for playback
        try {
          const recording = await audioRecorder.stopRecording();
          if (recording) {
            setLastUserRecording(recording);
            setShowReplayButton(true);
            setTimeout(() => setShowReplayButton(false), 15000);
          }
        } catch (err) {
          console.warn('[ContinuousMode] Could not save recording:', err);
        }
        
        // Clear input and transcript ref
        transcriptRef.current = '';
        setInputMessage('');
        
        // Show interpreting state
        setVoiceState(VoiceState.INTERPRETING);
        setVoiceStateLabel(VoiceStateLabels.interpreting);
        setConversationTurn('processing');
        
        // Send the message with voice flag and metrics
        if (transcript?.trim()) {
          handleSendMessage(null, transcript, true, voiceProcessorRef.current?.metrics);
        }
      },
      // NEW: Called when only noise detected for too long
      onNoiseOnlyDetected: () => {
        toast.info('Apenas ruído detectado. Toque no microfone para tentar novamente.', { duration: 3000 });
        setVoiceState(VoiceState.IDLE);
        setConversationTurn('idle');
        if (!continuousModeRef.current) {
          setIsListening(false);
          isListeningRef.current = false;
        }
      },
      // NEW: Called when STT confidence is low
      onLowConfidence: (confidence, transcript) => {
        console.log('[ChatRoom] Low confidence detected:', confidence, transcript);
        // Just log for now - backend will decide whether to ask for repetition
      },
      // NEW: Called when noise environment changes
      onEnvironmentChange: (envType, noiseLevel) => {
        console.log('[ChatRoom] Environment changed to:', envType, noiseLevel);
        // Show toast for significant changes
        if (envType === 'very_noisy') {
          toast.warning('Ambiente muito barulhento detectado. Tente um local mais silencioso.', { duration: 4000 });
        } else if (envType === 'noisy') {
          toast.info('Ambiente com ruído. A Lira está filtrando os ruídos.', { duration: 3000 });
        }
      }
    });
  };

  /**
   * Start Continuous Conversation Mode (FULLY AUTOMATIC with Intelligent Turn Detection)
   * 
   * Flow: Listen → Turn Detected → Send → Lira Responds → Auto-Listen (loop)
   * Barge-in: User can interrupt Lira anytime by speaking
   */
  const startContinuousMode = async () => {
    console.log('[NaturalConversation] Starting fully automatic conversation...');
    
    setContinuousMode(true);
    continuousModeRef.current = true;
    setSessionStartTime(Date.now());
    setVoiceMetrics({
      totalTurns: 0,
      avgLatency: 0,
      clarificationsRequested: 0,
      transcriptionConfidences: [],
    });
    
    // Show welcome message for natural conversation
    toast.success('🎙️ Conversa Natural ativada! Fale naturalmente - a Lira responderá automaticamente.', { duration: 4000 });
    
    // Initialize voice processor with intelligent turn detection
    if (!voiceProcessorRef.current) {
      await initializeVoiceProcessor();
    }
    
    // Enable intelligent turn detection
    if (voiceProcessorRef.current) {
      voiceProcessorRef.current.setIntelligentTurnEnabled(true);
      voiceProcessorRef.current.updateConversationContext(messages);
    }
    
    // Start first turn - listening to user
    await startListeningInternal();
  };

  /**
   * Stop Continuous Conversation Mode
   */
  const stopContinuousMode = () => {
    console.log('[ContinuousMode] Stopping continuous conversation...');
    
    setContinuousMode(false);
    continuousModeRef.current = false;
    
    // Clear any pending auto-listen timer
    if (autoListenTimerRef.current) {
      clearTimeout(autoListenTimerRef.current);
      autoListenTimerRef.current = null;
    }
    
    // Stop current listening if active
    if (isListening && voiceProcessorRef.current) {
      voiceProcessorRef.current.stop();
    }
    
    // Stop any speech
    stopSpeaking();
    
    // Reset states
    setVoiceState(VoiceState.IDLE);
    setConversationTurn('idle');
    setIsListening(false);
    isListeningRef.current = false;
    
    // Calculate session duration
    const sessionDuration = sessionStartTime ? Math.round((Date.now() - sessionStartTime) / 1000) : 0;
    
    toast.success(`Conversa encerrada! ${voiceMetrics.totalTurns} turnos em ${sessionDuration}s`, { duration: 3000 });
  };

  /**
   * Toggle Continuous Mode (single button)
   */
  const toggleContinuousMode = () => {
    if (continuousMode) {
      stopContinuousMode();
    } else {
      startContinuousMode();
    }
  };

  /**
   * Change VAD sensitivity
   */
  const handleSensitivityChange = (newSensitivity) => {
    setVadSensitivity(newSensitivity);
    
    // Update existing voice processor if it exists
    if (voiceProcessorRef.current) {
      voiceProcessorRef.current.setSensitivity(newSensitivity);
    }
    
    // Show confirmation
    const label = VADSensitivityLabels[newSensitivity];
    toast.success(`Sensibilidade do VAD: ${label}`, { duration: 2000 });
    setShowVadSettings(false);
  };

  // Play user's own voice recording
  const playUserRecording = () => {
    if (!lastUserRecording?.url) {
      toast.error('Nenhuma gravação disponível');
      return;
    }
    
    if (userAudioRef.current) {
      userAudioRef.current.pause();
    }
    
    userAudioRef.current = new Audio(lastUserRecording.url);
    userAudioRef.current.onplay = () => setIsPlayingRecording(true);
    userAudioRef.current.onended = () => setIsPlayingRecording(false);
    userAudioRef.current.onerror = () => setIsPlayingRecording(false);
    userAudioRef.current.play();
  };

  // Show emotional feedback
  const showFeedback = (type, customMessage = null) => {
    const message = customMessage || getRandomFeedback(type);
    setShowEmotionalFeedback(message);
    setTimeout(() => setShowEmotionalFeedback(null), 3000);
  };

  const speakText = async (text, customSpeed = null) => {
    // Cancel any ongoing speech (Web Speech)
    if (synthRef.current) {
      synthRef.current.cancel();
    }
    
    // Stop any playing audio element (Premium TTS)
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    
    const targetLang = user?.target_language || user?.learning_language || 'English';
    
    // REGRA GLOBAL: Falar APENAS no idioma estudado, NUNCA a tradução
    let textToSpeak = text;
    
    // ===== COMPREHENSIVE TRANSLATION REMOVAL =====
    
    // 1. Remove translation in parentheses with Portuguese indicators
    textToSpeak = textToSpeak.replace(/\([^)]*(?:Oi|Olá|E aí|Como|Que|Sobre|Você|Eu|Muito|Tudo|Legal|Bom|Boa|Isso|Sim|Não|Certo|Exato|Nossa|Tipo|Então|Porque|Acho|Gosto|Quero|Tenho|Vou|Fui|Foi|Era|Está|Estou|Pode|Posso|Sou|Ser|Ter|Fazer|Dizer|Ver|Ir|Dar|Saber|Querer|Poder|Dever|Ficar|Precisar)[^)]*\)/gi, '');
    
    // 2. Remove labeled translations
    textToSpeak = textToSpeak.replace(/\n*\(?(?:Tradução|PORTUGUÊS|PORTUGUESE|PT|Em português|In Portuguese|Tradução:)[:\s]*[^)\n]+\)?/gi, '');
    
    // 3. Remove any remaining parentheses that look like translations (> 15 chars with Portuguese words)
    textToSpeak = textToSpeak.replace(/\([^)]{15,}\)/g, (match) => {
      const inner = match.toLowerCase();
      const ptWords = ['você', 'isso', 'como', 'muito', 'aqui', 'hoje', 'então', 'porque', 'acho', 'gosto', 'quero', 'tenho'];
      if (ptWords.some(w => inner.includes(w))) {
        return ''; // Remove Portuguese translation
      }
      return match; // Keep non-Portuguese content
    });
    
    // 4. Remove lines that start with parentheses and contain Portuguese
    const lines = textToSpeak.split('\n');
    const filteredLines = lines.filter(line => {
      const trimmed = line.trim();
      if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
        const inner = trimmed.toLowerCase();
        const ptWords = ['você', 'isso', 'como', 'muito', 'aqui', 'hoje', 'então', 'porque', 'acho'];
        if (ptWords.some(w => inner.includes(w))) {
          return false; // Skip this line
        }
      }
      return true;
    });
    textToSpeak = filteredLines.join('\n');
    
    // 5. For Chinese: extract only Chinese text
    if (targetLang === 'Chinese') {
      if (text.includes('CHINESE:')) {
        const chineseMatch = text.match(/CHINESE:\s*([\s\S]*?)(?=\n\n(?:PINYIN|PORTUGUÊS|PORTUGUESE)|$)/i);
        if (chineseMatch && chineseMatch[1]) {
          textToSpeak = chineseMatch[1].trim();
        }
      }
      // Remove any Portuguese text that might remain
      textToSpeak = textToSpeak.replace(/\n*\(?(?:PORTUGUÊS|PORTUGUESE)[:\s]*[^)\n]+\)?/gi, '');
    }
    
    // Final cleanup
    textToSpeak = textToSpeak.replace(/\n{3,}/g, '\n\n'); // Max 2 consecutive newlines
    textToSpeak = textToSpeak.trim();
    
    // Don't speak if empty after cleaning
    if (!textToSpeak) return;
    
    // Store for replay
    setLastSpokenText(textToSpeak);
    
    // Use custom speed or state speed
    const speed = customSpeed !== null ? customSpeed : voiceSpeed;
    
    // BARGE-IN SUPPORT: Notify voice processor that Lira is speaking
    if (voiceProcessorRef.current) {
      voiceProcessorRef.current.setLiraSpeaking(true);
    }
    
    // Use hybrid TTS (Premium OpenAI or Free Web Speech)
    try {
      const result = await speakHybrid(textToSpeak, {
        language: targetLang,
        gender: voiceGender,
        speed: speed,
        onStart: () => {
          setIsSpeaking(true);
          setIsAvatarSpeaking(true);
          setConversationTurn('lira');
          setVoiceState(VoiceState.SPEAKING);
        },
        onEnd: () => {
          setIsSpeaking(false);
          setIsAvatarSpeaking(false);
          
          // Notify voice processor that Lira stopped speaking
          if (voiceProcessorRef.current) {
            voiceProcessorRef.current.setLiraSpeaking(false);
          }
          
          // AUTO-LISTEN: If continuous mode is active, start listening after Lira finishes
          if (continuousModeRef.current && autoListenAfterSpeak) {
            console.log('[ContinuousMode] Lira finished speaking, preparing to listen...');
            setVoiceState(VoiceState.WAITING_TO_LISTEN);
            setConversationTurn('waiting');
            
            // Small delay before auto-listening (let user process what was said)
            autoListenTimerRef.current = setTimeout(() => {
              if (continuousModeRef.current) {
                console.log('[ContinuousMode] Auto-starting listening...');
                startListeningInternal();
              }
            }, 600); // 600ms pause - slightly faster for natural flow
          } else {
            setVoiceState(VoiceState.IDLE);
            setConversationTurn('idle');
          }
        },
        onError: (err) => {
          console.error('Speech error:', err);
          setIsSpeaking(false);
          setIsAvatarSpeaking(false);
          setVoiceState(VoiceState.IDLE);
          setConversationTurn('idle');
          
          // Notify voice processor that Lira stopped speaking
          if (voiceProcessorRef.current) {
            voiceProcessorRef.current.setLiraSpeaking(false);
          }
        }
      });
      
      // Store audio element reference if it's an Audio object (premium)
      if (result instanceof Audio) {
        currentAudioRef.current = result;
      }
    } catch (error) {
      console.error('speakText error:', error);
      setIsSpeaking(false);
      setIsAvatarSpeaking(false);
      setVoiceState(VoiceState.IDLE);
      setConversationTurn('idle');
      
      // Notify voice processor that Lira stopped speaking
      if (voiceProcessorRef.current) {
        voiceProcessorRef.current.setLiraSpeaking(false);
      }
    }
  };

  // Replay last spoken text with specific speed
  const replayWithSpeed = (speed) => {
    if (lastSpokenText) {
      speakText(lastSpokenText, speed);
    }
  };

  const stopSpeaking = () => {
    // Stop Web Speech API
    synthRef.current.cancel();
    // Stop Premium TTS audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    setIsSpeaking(false);
    setIsAvatarSpeaking(false);
  };

  const handleQuickLanguageSwitch = async (language) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/auth/update-language?target_language=${language}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      toast.success(`AI will now respond in ${language}`);
    } catch (error) {
      toast.error('Falha ao atualizar idioma');
    }
  };

  const handleImproveSentence = async (messageContent, messageId) => {
    setImprovingMessageId(messageId);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chat/improve`,
        { message: messageContent, conversation_id: conversationId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Map improvement types to labels
      const improvementLabels = {
        grammar: '📝 Gramática',
        fluency: '🌊 Fluidez',
        clarity: '💡 Clareza',
        vocabulary: '📚 Vocabulário'
      };
      
      // Build improvement badges
      const improvements = response.data.improvements || [];
      
      // Show detailed improvement toast
      toast.success(
        <div className="space-y-2">
          <p className="font-semibold text-sm">✨ Versão melhorada:</p>
          <p className="text-base bg-lime/20 p-2 rounded-lg">{response.data.improved}</p>
          {response.data.translation && (
            <p className="text-xs text-stone/70 border-t border-forest/10 pt-2">
              {response.data.translation}
            </p>
          )}
          {improvements.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {improvements.map((imp, i) => (
                <span key={i} className="text-[10px] bg-forest/10 text-forest px-2 py-0.5 rounded-full">
                  {improvementLabels[imp] || imp}
                </span>
              ))}
            </div>
          )}
        </div>,
        { duration: 10000 }
      );
    } catch (error) {
      toast.error('Falha ao melhorar frase');
    } finally {
      setImprovingMessageId(null);
    }
  };

  const handleSaveWord = async (content, translation, msgId) => {
    setSavingWordId(msgId);
    try {
      const token = localStorage.getItem('token');
      const targetLang = user?.target_language || user?.learning_language || 'English';
      
      await axios.post(
        `${API}/vocabulary/save`,
        { 
          word: content.substring(0, 200),
          translation: translation || content.substring(0, 200),
          language: targetLang
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Frase salva no vocabulário!');
    } catch (error) {
      toast.error('Falha ao salvar palavra');
    } finally {
      setSavingWordId(null);
    }
  };

  const getLanguageFlag = (lang) => {
    const flags = {
      'English': '🇬🇧',
      'Spanish': '🇪🇸',
      'French': '🇫🇷',
      'German': '🇩🇪',
      'Chinese': '🇨🇳'
    };
    return flags[lang] || '🌐';
  };

  // Premium Lira avatar images based on voice gender
  const avatarImages = {
    female: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face',
    male: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face',
    // Legacy support - map to premium images
    professional_teacher: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face',
    friendly_young: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face',
    casual_native: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face',
    neutral_assistant: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face',
    lira: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200&h=200&fit=crop&crop=face',
  };
  
  // Get Lira's avatar based on voice gender
  const getLiraAvatar = () => {
    return avatarImages[voiceGender] || avatarImages.female;
  };
  
  // Lira is the AI companion name - always "Lira"
  const currentTutorName = 'Lira';
  
  // Listen for voice gender changes from Settings
  useEffect(() => {
    const handleVoiceChange = (event) => {
      const { gender } = event.detail;
      setVoiceGender(gender);
    };
    
    window.addEventListener('liraVoiceChanged', handleVoiceChange);
    
    // Also check localStorage on mount
    const savedGender = localStorage.getItem('lira_voice_gender');
    if (savedGender && (savedGender === 'female' || savedGender === 'male')) {
      setVoiceGender(savedGender);
    }
    
    return () => {
      window.removeEventListener('liraVoiceChanged', handleVoiceChange);
    };
  }, []);

  return (
    <div className="min-h-screen bg-sand flex flex-col" data-testid="chat-room">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-red-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <WifiOff className="w-4 h-4" />
          <span>Sem conexão com a internet</span>
        </div>
      )}
      
      {/* Header */}
      <header className="bg-white border-b border-forest/10 p-4">
        <div className="container mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="relative">
                <img
                  src={getLiraAvatar()}
                  alt="Lira"
                  className={`w-12 h-12 rounded-full object-cover border-2 shadow-soft transition-all ${
                    isAvatarSpeaking 
                      ? 'border-lime ring-4 ring-lime/30 animate-pulse scale-105' 
                      : 'border-lime'
                  }`}
                />
                {/* Speaking indicator */}
                {isAvatarSpeaking ? (
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-lime rounded-full border-2 border-white flex items-center justify-center">
                    <div className="flex gap-0.5">
                      <span className="w-1 h-2 bg-forest rounded-full animate-bounce" style={{animationDelay: '0ms'}}></span>
                      <span className="w-1 h-3 bg-forest rounded-full animate-bounce" style={{animationDelay: '150ms'}}></span>
                      <span className="w-1 h-2 bg-forest rounded-full animate-bounce" style={{animationDelay: '300ms'}}></span>
                    </div>
                  </div>
                ) : (
                  <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-lime rounded-full border-2 border-white"></div>
                )}
              </div>
              <div>
                <h2 className="font-medium text-forest">
                  {isAvatarSpeaking ? 'Falando...' : currentTutorName}
                </h2>
                <p className="text-xs text-stone">
                  {conversationMode.label}
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Quick Language Switch */}
            <div className="flex items-center gap-2 px-3 py-2 bg-sand rounded-full">
              <Globe className="w-4 h-4 text-forest" />
              <Select
                value={user?.target_language || user?.learning_language || 'English'}
                onValueChange={handleQuickLanguageSwitch}
              >
                <SelectTrigger 
                  data-testid="quick-language-switch" 
                  className="h-8 w-[130px] border-0 bg-transparent focus:ring-0 px-2"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent 
                  className="z-[9999] bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-forest/10 p-1 min-w-[180px]"
                  position="popper"
                  sideOffset={5}
                >
                  <SelectItem value="English" className="rounded-lg px-3 py-2.5 cursor-pointer hover:bg-sand focus:bg-sand focus:outline-none">
                    🇬🇧 English
                  </SelectItem>
                  <SelectItem value="Spanish" className="rounded-lg px-3 py-2.5 cursor-pointer hover:bg-sand focus:bg-sand focus:outline-none">
                    🇪🇸 Spanish
                  </SelectItem>
                  <SelectItem value="French" className="rounded-lg px-3 py-2.5 cursor-pointer hover:bg-sand focus:bg-sand focus:outline-none">
                    🇫🇷 French
                  </SelectItem>
                  <SelectItem value="German" className="rounded-lg px-3 py-2.5 cursor-pointer hover:bg-sand focus:bg-sand focus:outline-none">
                    🇩🇪 German
                  </SelectItem>
                  <SelectItem value="Chinese" className="rounded-lg px-3 py-2.5 cursor-pointer hover:bg-sand focus:bg-sand focus:outline-none">
                    🇨🇳 Chinese
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Lira Voice Indicator - Voice configured in Settings */}
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-sand rounded-full text-xs text-stone">
              <span>{voiceGender === 'female' ? '👩' : '👨'}</span>
              <span className="hidden sm:inline">Lira</span>
            </div>
            
            {/* Voice Speed Selector */}
            <div className="flex items-center gap-1 px-2 py-1.5 bg-sand rounded-full" data-testid="voice-speed-selector">
              <button
                onClick={() => setVoiceSpeed(0.5)}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  voiceSpeed === 0.5 
                    ? 'bg-forest text-sand' 
                    : 'text-stone hover:bg-white/50'
                }`}
                title="Muito devagar (0.5x)"
                data-testid="speed-05"
              >
                🐢
              </button>
              <button
                onClick={() => setVoiceSpeed(0.75)}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  voiceSpeed === 0.75 
                    ? 'bg-forest text-sand' 
                    : 'text-stone hover:bg-white/50'
                }`}
                title="Devagar (0.75x)"
                data-testid="speed-075"
              >
                🐇
              </button>
              <button
                onClick={() => setVoiceSpeed(0.85)}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  voiceSpeed === 0.85 
                    ? 'bg-forest text-sand' 
                    : 'text-stone hover:bg-white/50'
                }`}
                title="Normal (0.85x)"
                data-testid="speed-085"
              >
                🏃
              </button>
              <button
                onClick={() => setVoiceSpeed(1.0)}
                className={`px-2 py-1 rounded-full text-xs font-medium transition-all ${
                  voiceSpeed === 1.0 
                    ? 'bg-forest text-sand' 
                    : 'text-stone hover:bg-white/50'
                }`}
                title="Rápido (1x)"
                data-testid="speed-10"
              >
                ⚡
              </button>
            </div>
            
            {/* Translation Toggle */}
            {user?.native_language !== (user?.target_language || user?.learning_language) && (
              <Button
                onClick={() => setShowTranslations(!showTranslations)}
                variant="ghost"
                size="sm"
                className="text-forest hover:text-forest/80"
                data-testid="toggle-translations"
              >
                {showTranslations ? '🌐' : '🌐'}
              </Button>
            )}
            
            {/* End Session Button */}
            <Button
              onClick={() => {
                if (conversationId && messages.length > 2) {
                  navigate(`/session/${conversationId}/complete`);
                } else {
                  navigate('/home');
                }
              }}
              variant="ghost"
              className="text-clay hover:text-clay/80 hover:bg-clay/10"
              data-testid="end-session-btn"
              title="Encerrar sessão"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
                <line x1="9" x2="15" y1="15" y2="9"/>
              </svg>
            </Button>
            
            <Button
              onClick={() => navigate('/settings')}
              variant="ghost"
              data-testid="settings-btn"
            >
              <Settings className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Topic Selector - REMOVED: Lira now starts the conversation automatically */}
      
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="container mx-auto max-w-4xl space-y-4">
          
          {/* Auto-start greeting if no messages */}
          {messages.length === 0 && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-12"
            >
              <div className="w-20 h-20 bg-lime/20 rounded-full flex items-center justify-center mb-6">
                <MessageCircle className="w-10 h-10 text-forest" />
              </div>
              <h2 className="font-fraunces text-xl font-semibold text-forest mb-2">
                Iniciando conversa...
              </h2>
              <p className="text-stone text-sm mb-4">
                Lira vai começar a conversa com você
              </p>
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-lime rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-lime rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-lime rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </motion.div>
          )}
          
          {/* Guided Mode Indicator */}
          {isGuided && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-lime/20 to-lime/10 border-l-4 border-lime rounded-2xl p-4 mb-6"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-lime rounded-full flex items-center justify-center">
                  <Play className="w-5 h-5 text-forest" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-forest mb-1">Modo de Prática Guiada</h3>
                  <p className="text-sm text-forest/70">
                    Vou guiar você nesta conversa passo a passo. Responda naturalmente e eu ajudo você!
                  </p>
                  <button
                    onClick={() => setIsGuided(false)}
                    className="text-xs text-forest/60 hover:text-forest mt-2 underline"
                  >
                    Mudar para conversa livre →
                  </button>
                </div>
              </div>
            </motion.div>
          )}
          
          <AnimatePresence>
            {messages.map((msg, index) => (
              <motion.div
                key={msg.id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${msg.role}-${index}`}
              >
                <div className="max-w-[80%] space-y-3">
                  {/* Correction Card (shown before AI response) */}
                  {msg.corrections && msg.role === 'assistant' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="bg-clay/10 border-l-4 border-clay rounded-2xl p-4 mb-2"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <span className="text-xl">✏️</span>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-clay mb-1">Correção</p>
                          <p className="text-sm text-forest line-through opacity-60">
                            {msg.corrections.original}
                          </p>
                          <p className="text-sm text-forest font-medium mt-1">
                            {msg.corrections.corrected}
                          </p>
                          {msg.corrections.tip && (
                            <p className="text-xs text-forest/70 mt-2 italic">
                              💡 {msg.corrections.tip}
                            </p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Main Message */}
                  <div
                    className={`rounded-2xl px-6 py-4 shadow-soft ${
                      msg.role === 'user'
                        ? 'bg-forest text-sand rounded-tr-sm'
                        : 'bg-white text-forest border border-forest/10 rounded-tl-sm'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{cleanMainMessage(msg.content)}</p>
                    
                    {/* Translation based on conversation mode - CLEAN, NO EMOJIS */}
                    {msg.role === 'assistant' && msg.translation && 
                     user?.native_language !== (user?.target_language || user?.learning_language) && (
                      <div className="mt-3">
                        {/* Beginner: Auto-show translation */}
                        {conversationMode.mode === 'assisted' ? (
                          <div className="pt-3 border-t border-forest/10">
                            <p className="text-sm text-stone/70">
                              {cleanTranslation(msg.translation)}
                            </p>
                          </div>
                        ) : conversationMode.mode === 'conversation' ? (
                          /* Intermediate: Optional translation via button */
                          visibleTranslations[msg.id] ? (
                            <div className="pt-3 border-t border-forest/10">
                              <p className="text-sm text-stone/70">
                                {cleanTranslation(msg.translation)}
                              </p>
                            </div>
                          ) : (
                            <button
                              onClick={() => toggleTranslation(msg.id)}
                              className="text-xs text-stone/40 hover:text-forest/60 flex items-center gap-1 transition-colors mt-1"
                            >
                              <Globe className="w-3 h-3" />
                              Tradução
                            </button>
                          )
                        ) : (
                          /* Advanced: Hidden translation - only on explicit request */
                          visibleTranslations[msg.id] && (
                            <div className="pt-3 border-t border-forest/10">
                              <p className="text-sm text-stone/70">
                                {cleanTranslation(msg.translation)}
                              </p>
                            </div>
                          )
                        )}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {msg.role === 'assistant' && (
                        <>
                          {/* Speed Controls for Listening */}
                          <div className="flex items-center gap-1 bg-sand/50 rounded-lg px-2 py-1">
                            <button
                              onClick={() => { setLastSpokenText(msg.content); replayWithSpeed(0.5); }}
                              className="flex items-center gap-1 text-xs hover:text-forest transition-colors px-1.5 py-0.5 rounded hover:bg-white/50"
                              data-testid={`speed-slow-btn-${index}`}
                              disabled={isSpeaking}
                              title="Muito devagar"
                            >
                              🐢
                            </button>
                            <button
                              onClick={() => { setLastSpokenText(msg.content); replayWithSpeed(0.75); }}
                              className="flex items-center gap-1 text-xs hover:text-forest transition-colors px-1.5 py-0.5 rounded hover:bg-white/50"
                              data-testid={`speed-medium-btn-${index}`}
                              disabled={isSpeaking}
                              title="Devagar"
                            >
                              🐇
                            </button>
                            <button
                              onClick={() => speakText(msg.content)}
                              className="flex items-center gap-1 text-xs hover:text-lime transition-colors px-1.5 py-0.5 rounded hover:bg-white/50"
                              data-testid={`play-audio-btn-${index}`}
                              disabled={isSpeaking}
                              title="Ouvir"
                            >
                              <Volume2 className="w-3 h-3" />
                              <span>{isSpeaking ? '...' : 'Ouvir'}</span>
                            </button>
                          </div>
                          <button
                            onClick={() => handleSaveWord(msg.content, msg.translation, msg.id)}
                            disabled={savingWordId === msg.id}
                            className="flex items-center gap-1 text-xs hover:text-amber-500 transition-colors px-2 py-1 rounded-lg hover:bg-amber-50"
                            data-testid={`save-word-btn-${index}`}
                          >
                            <Star className={`w-3 h-3 ${savingWordId === msg.id ? 'fill-amber-400 text-amber-400' : ''}`} />
                            <span>{savingWordId === msg.id ? 'Salvando...' : 'Salvar'}</span>
                          </button>
                        </>
                      )}
                      
                      {msg.role === 'user' && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleImproveSentence(msg.content, msg.id)}
                            disabled={improvingMessageId === msg.id}
                            className="flex items-center gap-1 text-xs hover:text-lime transition-colors px-2 py-1 rounded-lg hover:bg-sand/50"
                            data-testid={`improve-btn-${index}`}
                          >
                            <span>✨</span>
                            <span>{improvingMessageId === msg.id ? 'Melhorando...' : 'Melhorar'}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          
          {/* Progress Message */}
          <AnimatePresence>
            {showProgressMessage && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="fixed bottom-32 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-full shadow-lg z-50"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">🎉</span>
                  <span className="font-medium">
                    {messageCount === 5 && 'Ótimo começo! Continue assim!'}
                    {messageCount === 10 && 'Você completou 10 mensagens! Excelente!'}
                    {messageCount === 20 && 'Incrível! 20 mensagens de prática!'}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Emotional Feedback Toast */}
          <AnimatePresence>
            {showEmotionalFeedback && !showProgressMessage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: -20 }}
                className="fixed bottom-32 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-lime to-emerald-400 text-forest px-5 py-2.5 rounded-full shadow-lg z-50 font-medium text-sm"
              >
                {showEmotionalFeedback}
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Replay Voice Button - Always Visible, discrete floating button */}
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={playUserRecording}
            disabled={!lastUserRecording || isPlayingRecording}
            className={`fixed bottom-44 right-4 rounded-full shadow-lg z-50 flex items-center justify-center gap-1.5 transition-all ${
              lastUserRecording 
                ? 'bg-purple-500 hover:bg-purple-600 text-white px-3 py-2' 
                : 'bg-gray-100 text-gray-400 px-3 py-2 opacity-60'
            } ${isPlayingRecording ? 'ring-2 ring-purple-300 ring-offset-2' : ''}`}
            data-testid="replay-voice-floating-btn"
            title={lastUserRecording ? (isPlayingRecording ? 'Tocando...' : 'Ouvir minha voz') : 'Grave algo primeiro'}
          >
            <Headphones className={`w-4 h-4 ${isPlayingRecording ? 'animate-pulse' : ''}`} />
            <span className="text-xs font-medium">{isPlayingRecording ? '...' : 'Minha voz'}</span>
          </motion.button>
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Beginner Suggestions - Dynamic */}
      {user?.level === 'beginner' && !loading && !isListening && !isSpeaking && (
        <div className="bg-white border-t border-forest/10 py-3 px-4">
          <div className="container mx-auto max-w-4xl">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-stone">Sugestões de resposta:</p>
              {loadingSuggestions && (
                <span className="text-xs text-stone/60 animate-pulse">Atualizando...</span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {(dynamicSuggestions.length > 0 ? dynamicSuggestions : getDefaultSuggestions(user?.target_language || user?.learning_language)).map((suggestion, i) => (
                <motion.button
                  key={`${suggestion}-${i}`}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="bg-sand hover:bg-lime/20 text-forest text-sm px-4 py-2 rounded-full transition-all border border-forest/10 hover:border-lime hover:shadow-md active:scale-95"
                  data-testid={`suggestion-btn-${i}`}
                >
                  {suggestion}
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Voice Processing Feedback - Advanced States */}
      {(isListening || voiceState === VoiceState.PROCESSING || voiceState === VoiceState.INTERPRETING) && (
        <div className="bg-white border-t border-forest/10 py-4">
          <div className="container mx-auto max-w-4xl flex flex-col items-center gap-3">
            {/* Voice Visualizer with volume indicator */}
            <div className="relative">
              <VoiceVisualizer isActive={isListening || voiceState !== VoiceState.IDLE} />
              
              {/* Volume level indicator */}
              {isListening && (
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1.5 bg-forest/10 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-lime rounded-full"
                    animate={{ width: `${Math.min(voiceVolume * 400, 100)}%` }}
                    transition={{ duration: 0.1 }}
                  />
                </div>
              )}
            </div>
            
            <div className="text-center">
              {/* Dynamic state labels */}
              {voiceState === VoiceState.LISTENING && (
                <>
                  <div className="flex items-center justify-center gap-2">
                    <Mic className="w-4 h-4 text-lime animate-pulse" />
                    <p className="text-sm text-forest font-medium">Ouvindo...</p>
                  </div>
                  <p className="text-xs text-stone mt-1">Fale naturalmente • Aguardarei você terminar</p>
                </>
              )}
              
              {voiceState === VoiceState.DETECTING_PAUSE && (
                <>
                  <div className="flex items-center justify-center gap-2">
                    <AudioWaveform className="w-4 h-4 text-amber-500" />
                    <p className="text-sm text-forest font-medium">Aguardando...</p>
                  </div>
                  <p className="text-xs text-stone mt-1">Detectando pausa natural</p>
                </>
              )}
              
              {voiceState === VoiceState.PROCESSING && (
                <>
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-forest/30 border-t-forest rounded-full animate-spin" />
                    <p className="text-sm text-forest font-medium">Processando...</p>
                  </div>
                  <p className="text-xs text-stone mt-1">Analisando sua fala</p>
                </>
              )}
              
              {voiceState === VoiceState.INTERPRETING && (
                <>
                  <div className="flex items-center justify-center gap-2">
                    <Brain className="w-4 h-4 text-purple-500 animate-pulse" />
                    <p className="text-sm text-forest font-medium">Interpretando...</p>
                  </div>
                  <p className="text-xs text-stone mt-1">Entendendo o que você quis dizer</p>
                </>
              )}
            </div>
            
            {/* Show transcript */}
            {inputMessage && (
              <div className="bg-lime/10 rounded-xl px-4 py-2 max-w-md">
                <p className="text-sm text-forest italic">&ldquo;{inputMessage}&rdquo;</p>
              </div>
            )}
            
            {/* Noise suppression indicator */}
            {noiseSuppressionActive && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded-full"
              >
                🔇 Ruído ignorado
              </motion.div>
            )}
          </div>
        </div>
      )}

      {isSpeaking && (
        <div className="bg-white border-t border-forest/10 py-4">
          <div className="container mx-auto max-w-4xl flex flex-col items-center gap-2">
            <VoiceVisualizer isActive={isSpeaking} />
            <Button
              onClick={stopSpeaking}
              variant="outline"
              size="sm"
              className="rounded-full"
              data-testid="stop-speaking-btn"
            >
              Parar
            </Button>
          </div>
        </div>
      )}

      {/* Preparing Speech Indicator (3s delay) */}
      {isPreparingSpeech && !isSpeaking && (
        <div className="bg-gradient-to-r from-lime/20 to-lime/10 border-t-2 border-lime py-6">
          <div className="container mx-auto max-w-4xl flex flex-col items-center gap-3">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-forest rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <div className="w-3 h-3 bg-forest rounded-full animate-bounce" style={{ animationDelay: '200ms' }} />
              <div className="w-3 h-3 bg-forest rounded-full animate-bounce" style={{ animationDelay: '400ms' }} />
            </div>
            <p className="text-sm font-medium text-forest">Pensando na resposta... 💭</p>
            <p className="text-xs text-stone">Já já eu falo!</p>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="bg-white border-t border-forest/10 p-3 sm:p-4 safe-bottom">
        <div className="container mx-auto max-w-4xl">
          {/* Mic Language Selector */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="text-xs text-stone">Falar em:</span>
            <div className="flex bg-sand rounded-full p-1">
              <button
                type="button"
                onClick={() => setMicLanguage('native')}
                className={`px-3 py-1 text-xs rounded-full transition-all ${
                  micLanguage === 'native'
                    ? 'bg-forest text-sand'
                    : 'text-stone hover:text-forest'
                }`}
                data-testid="mic-lang-native"
              >
                🇧🇷 Português
              </button>
              <button
                type="button"
                onClick={() => setMicLanguage('target')}
                className={`px-3 py-1 text-xs rounded-full transition-all ${
                  micLanguage === 'target'
                    ? 'bg-forest text-sand'
                    : 'text-stone hover:text-forest'
                }`}
                data-testid="mic-lang-target"
              >
                {user?.target_language === 'English' && '🇺🇸'}
                {user?.target_language === 'Spanish' && '🇪🇸'}
                {user?.target_language === 'German' && '🇩🇪'}
                {user?.target_language === 'French' && '🇫🇷'}
                {user?.target_language === 'Chinese' && '🇨🇳'}
                {!['English', 'Spanish', 'German', 'French', 'Chinese'].includes(user?.target_language) && '🌍'}
                {' '}{user?.target_language || user?.learning_language || 'English'}
              </button>
            </div>
          </div>

          {/* Continuous Mode Indicator */}
          {continuousMode && (
            <div className="flex items-center justify-center gap-3 mb-3 px-4 py-2.5 bg-gradient-to-r from-lime/20 to-forest/10 rounded-xl border border-lime/30">
              <div className="flex items-center gap-2">
                {conversationTurn === 'user' && (
                  <>
                    <div className="w-3 h-3 bg-lime rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-forest">Sua vez de falar...</span>
                  </>
                )}
                {conversationTurn === 'lira' && (
                  <>
                    <div className="w-3 h-3 bg-forest rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-forest">Lira está falando...</span>
                  </>
                )}
                {conversationTurn === 'processing' && (
                  <>
                    <div className="w-3 h-3 bg-amber-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-forest">Processando...</span>
                  </>
                )}
                {conversationTurn === 'waiting' && (
                  <>
                    <div className="w-3 h-3 bg-sky-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-forest">Preparando...</span>
                  </>
                )}
              </div>
              <button
                onClick={stopContinuousMode}
                className="ml-auto px-3 py-1 text-xs bg-clay/80 hover:bg-clay text-white rounded-full transition-colors"
                data-testid="stop-continuous-btn"
                aria-label="Encerrar modo de conversa contínua"
              >
                Encerrar
              </button>
            </div>
          )}

          {/* Pause Feedback - "Ainda ouvindo..." */}
          {pauseFeedback.show && (
            <div className="flex items-center justify-center gap-2 mb-2 px-4 py-2 bg-amber-50 rounded-lg border border-amber-200 animate-pulse">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span className="text-sm text-amber-700">{pauseFeedback.message}</span>
              <span className="text-xs text-amber-600 ml-1">
                (continue falando se quiser)
              </span>
            </div>
          )}

          {/* VAD Sensitivity Selector - Show only when not listening */}
          {!isListening && !continuousMode && (
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-stone">Tempo de pausa:</span>
                <div className="flex gap-1">
                  {Object.entries(VADSensitivity).map(([key, value]) => (
                    <button
                      key={value}
                      onClick={() => handleSensitivityChange(value)}
                      className={`px-2 py-1 text-xs rounded-full transition-all ${
                        vadSensitivity === value
                          ? 'bg-forest text-sand font-medium'
                          : 'bg-sand hover:bg-lime/50 text-stone'
                      }`}
                      title={VADSensitivityDescriptions[value]}
                      data-testid={`vad-sensitivity-${value}`}
                    >
                      {VADSensitivityLabels[value]}
                    </button>
                  ))}
                </div>
              </div>
              {user?.level === 'beginner' && vadSensitivity !== VADSensitivity.PATIENT && (
                <button
                  onClick={() => handleSensitivityChange(VADSensitivity.PATIENT)}
                  className="text-xs text-blue-600 hover:text-blue-700"
                >
                  Dica: use Paciente
                </button>
              )}
            </div>
          )}

          <form onSubmit={handleSendMessage} className="flex items-center gap-2 sm:gap-3">
            {/* Continuous Mode Button - Only show when not in continuous mode */}
            {!continuousMode && !isListening && (
              <Button
                type="button"
                onClick={startContinuousMode}
                data-testid="continuous-mode-btn"
                disabled={loading || isSpeaking}
                className="rounded-full p-3 sm:p-4 transition-all flex-shrink-0 bg-gradient-to-br from-forest to-forest/80 hover:from-forest/90 hover:to-forest/70 text-lime shadow-lg"
                title="Iniciar conversa contínua"
                aria-label="Iniciar modo de conversa contínua com a Lira"
              >
                <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
              </Button>
            )}
            
            {/* Regular Mic Button */}
            <Button
              type="button"
              onClick={toggleListening}
              data-testid="voice-record-btn"
              disabled={loading || isSpeaking}
              className={`rounded-full p-3 sm:p-4 transition-all flex-shrink-0 ${
                isListening || continuousMode
                  ? 'bg-clay hover:bg-clay/90 text-white animate-pulse'
                  : 'bg-lime hover:bg-lime/90 text-forest'
              }`}
              title={continuousMode ? "Encerrar conversa" : (isListening ? "Parar gravação" : "Gravar mensagem")}
              aria-label={continuousMode ? "Encerrar conversa contínua" : (isListening ? "Parar gravação de voz" : "Iniciar gravação de voz")}
            >
              {isListening || continuousMode ? <MicOff className="w-5 h-5 sm:w-6 sm:h-6" /> : <Mic className="w-5 h-5 sm:w-6 sm:h-6" />}
            </Button>
            <Input
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={continuousMode ? 'Modo conversa ativo - fale com a Lira!' : (micLanguage === 'native' ? 'Digite em português ou no idioma escolhido...' : `Mensagem em ${user?.target_language || user?.learning_language || 'English'}...`)}
              data-testid="chat-input"
              disabled={loading || isListening || isSpeaking || continuousMode}
              className="flex-1 bg-sand border-forest/10 rounded-full px-4 sm:px-6 py-3 sm:py-4 focus:ring-2 focus:ring-lime/50 text-base"
            />
            <Button
              type="submit"
              data-testid="send-message-btn"
              disabled={loading || !inputMessage.trim() || isListening || isSpeaking || continuousMode}
              className="bg-forest hover:bg-forest/90 text-sand rounded-full p-3 sm:p-4 transition-all flex-shrink-0"
            >
              <Send className="w-5 h-5 sm:w-6 sm:h-6" />
            </Button>
          </form>
          
          {/* Message Counter */}
          {planStatus && !planStatus.is_premium && (
            <div className="flex items-center justify-center gap-2 mt-2">
              <p className="text-xs text-stone">
                {planStatus.messages_remaining > 0 
                  ? `${planStatus.messages_remaining} mensagens restantes hoje`
                  : 'Limite diário atingido'}
              </p>
              {planStatus.messages_remaining <= 5 && planStatus.messages_remaining > 0 && (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Upgrade
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgradeSuccess={() => {
          fetchPlanStatus();
          refreshUser?.();
        }}
      />
    </div>
  );
};

export default ChatRoom;